/**
 * Browse's own decisions — when a row is in a window, which chip it belongs
 * to, which day it sits under, whether the search box likes it — in a file
 * with no imports, for the reason `today.ts`, `rowMeta.ts`, `filmRank.ts` and
 * `away.ts` all give theirs: the root suite loads these files directly and
 * Node's ESM resolver will not guess the extension a bundler would, so an
 * `import … from './calendar'` here would make the whole file untestable
 * offline (CLAUDE.md: put a decision in a dependency-free file and it can be
 * tested from the Worker's suite). `FeedRow` and `Showing` below are
 * `today.ts`'s own trick, restated: they are declared here rather than
 * imported from `./types`, because that import would be erased by Node's
 * type stripping today and is one loader change away from not being.
 *
 * F3's "chronological under sticky day headings" and F2's "two controls,
 * when and what kind" are both windowing decisions, the same shape as H1's on
 * Today — which is why this file exists rather than living inside
 * `Browse.tsx` where `npm test` cannot see it.
 *
 * The one import is `./search.ts`, named with its extension so Node loads it
 * and dependency-free itself — the search haystack Today shares (a1-05).
 */
import { matchesSearch, type Searchable } from './search.ts'
import { scoreOf, urgencyLift } from './rankBase.ts'
import { releaseOn } from './releaseOn.ts'
import { FALLBACK_ZONE, clockIn, dayIn, hereZone, zoneOfCity } from './zone.ts'

// ---------------------------------------------------------------------------
// The shapes this file reads, declared rather than imported
// ---------------------------------------------------------------------------

export interface FeedRow {
  id: string
  title: string
  type: string
  discipline?: string | null
  run_start?: string | null
  run_end?: string | null
  published_on?: string | null
  next_showing?: string | null
  next_opening?: string | null
  showing_count?: number | null
  venue?: string | null
  building?: string | null
  organisation?: string | null
  district?: string | null
  city?: string | null
  performers?: Record<string, string> | null
  classical?: { programme?: { composer?: string | null; work?: string | null }[] } | null
  /** WP 8.3's own field: Stage D's score, `browseRank`'s (WP 8.0) base. */
  score?: number | null
  /** WP 6.3's own field, for the capped cluster's scored-films-first rule
   *  below — `perKind.ts` (WP 6.0) already carries the identical shape for
   *  Today's picks, restated rather than imported for the same reason every
   *  other field on this interface is: this file may not import that one.
   *  `de_release_on` (WP 8.3) is the same reason: `feed_lite` carries no
   *  top-level column of that name, only the one `mergeFilmFacts` writes
   *  inside `film` — `browseRank.ts`'s own `RankRow` agrees as of WP 8.3b,
   *  which fixed its top-level field of the same name for reading `undefined`
   *  on every real row. */
  film?: {
    rt_score?: number | null; metascore?: number | null; imdb_rating?: number | null
    de_release_on?: string | null
    /** C2.3: the Worker's per-country map (C4.3); `releaseOn` reads it. */
    release_on?: Record<string, string | null | undefined> | null
  } | null
  /** E1/E3, for the affinity lift below — `affinity.ts`'s own shape,
   *  restated because this file may not import it. */
  people?: { id: string }[] | null
  ensembles?: { id: string }[] | null
  /** A card standing for several entries (a film's set): every id it
   *  stands for, `entries.ts`'s `idsOf` restated. Its showings are all of
   *  theirs (a3-18). */
  group_ids?: string[] | null
}

/** One showing, structurally — `ShowingLite` satisfies it. Carries its own
 *  `entry_id` (unlike `rowMeta.ts`'s `Showing`) because this file is handed
 *  the whole window's showings at once rather than one row's own. */
export interface Showing {
  entry_id: string
  starts_at: string
  kind?: string | null
}

// ---------------------------------------------------------------------------
// Dates — `calendar.ts`'s and `today.ts`'s own rules, reproduced because this
// file may not import them. A `YYYY-MM-DD` is pinned to noon UTC and read
// back in UTC, so it never shifts a day for a reader in Berlin; a timestamp
// is read in Berlin wall-clock, because that is the time printed on the
// ticket.
// ---------------------------------------------------------------------------

const D = (s: string) => new Date(`${s}T12:00:00Z`)

// The zones (C2.1, trap T3): "now" — today, tonight, which window — is read in
// the current region's zone; a row's own timestamp in its town's region's.
// `zone.ts` builds one formatter per zone and memoises it, because `inWindow`
// asks both of every row — 3.5 s of self time on one When-chip change at CPU
// x4 when each call built its own (a4-13). An invalid date keeps the
// string/NaN `toLocale*String` gave rather than `format`'s `RangeError`.
const hereDay = (at: Date | string): string => dayIn(at, hereZone())
const hereHour = (at: Date | string): number => Number(clockIn(at, hereZone()).slice(0, 2))
const rowDay = (e: { city?: string | null }, at: string): string => dayIn(at, zoneOfCity(e.city))

/**
 * Berlin's calendar day, by name — **for the Worker**, which imports it
 * (`src/butler/context.ts`): the butler's days are Berlin's until C1.4 gives
 * it the person's region, and D3 keeps the operator's days on Berlin on
 * purpose. Nothing in the app reads it; the app asks `zone.ts`.
 */
export const berlinDay = (at: Date | string): string => dayIn(at, FALLBACK_ZONE)

const addDaysIso = (s: string, n: number) =>
  new Date(D(s).getTime() + n * 86400000).toISOString().slice(0, 10)

/** 0 Sunday … 6 Saturday, for a date-only string. */
const dayOfWeek = (isoDay: string) => D(isoDay).getUTCDay()

// ---------------------------------------------------------------------------
// Kind (F2's second control)
// ---------------------------------------------------------------------------

export type Kind =
  'all' | 'concert' | 'opera' | 'theatre' | 'exhibition' | 'film' | 'club' | 'gig' | 'quiz' | 'talk' | 'market'

/** Club and Gigs sit before Talks, as their lanes sit between Stage and
 *  Talks in `entries.ts` — and the butler's `kinds` enum is this array
 *  (`src/butler/tools/manifest.ts`), so a change here bumps its
 *  `MANIFEST_VERSION`. Quizzes (nightlife N6) follow the club nights: the
 *  same kind of evening out, a habit rather than a pick. */
export const KINDS: Exclude<Kind, 'all'>[] =
  ['concert', 'opera', 'theatre', 'exhibition', 'film', 'club', 'gig', 'quiz', 'talk', 'market']

/** `entries.ts`'s `GIG_DISCIPLINES`, restated for the no-imports rule. */
const GIG_DISCIPLINES = ['rock_pop', 'electronic', 'jazz']

/**
 * Which chip a row belongs to. Total (rule 2): every branch below is reached
 * by some row the app actually produces, and the last one — Talks — is also
 * where anything unclassified lands, the same job `REST` does in
 * `entries.ts`'s lane table.
 *
 * A different cut from `entries.ts`'s `LANES`, on purpose. The lanes put
 * opera in Orchestral, beside the concerts, because Home's quotas are about
 * volume; Browse's brief asks for "Opera and dance" as one chip, which is a
 * cut by discipline instead. Two screens are allowed to answer "what kind is
 * this" differently when they are asking it for different reasons — the bug
 * CLAUDE.md warns about is one value doing two jobs silently, not two
 * functions doing the same job on purpose.
 *
 * Club and Gigs (blind spots A2) are their own chips now that their lanes
 * are drawn: a club night is `club` whatever its discipline (SO36's are all
 * `other`), and a gig is a concert in one of the gig disciplines — the
 * lanes' own two rules. A jazz or electronic row that is not a concert
 * keeps landing where it did, in Concerts, rather than falling to Talks.
 */
export function kindOf(e: FeedRow): Exclude<Kind, 'all'> {
  const { type, discipline } = e

  // Nightlife N6 (D6): a pub quiz is its own chip, whatever its discipline —
  // before this it would have fallen through to Talks, the catch-all.
  if (type === 'quiz') return 'quiz'
  if (type === 'club_night') return 'club'
  // A festival (30 Sept): a music festival is a gig's kind of thing, any
  // other — a street fair, a garden day, Tag der Clubkultur — a market's.
  // `rowMeta.ts`'s `kindWord` gives both the word "Festival". Before this,
  // the fallback below called every one of them a talk (verify-6).
  if (type === 'festival') {
    if (!!discipline && GIG_DISCIPLINES.includes(discipline)) return 'gig'
    if (discipline === 'classical' || discipline === 'opera') return 'concert'
    return 'market'
  }
  // A market is a market whatever it is about: a design or antique fair has
  // `discipline: 'design'` and read as an exhibition, a record fair as a
  // concert, below. The discipline arms that follow are for rows whose type
  // says nothing (4 Oct 2026, Design Börse Berlin).
  if (type === 'market') return 'market'
  if (type === 'concert' && !!discipline && GIG_DISCIPLINES.includes(discipline)) return 'gig'
  if (type === 'concert' || discipline === 'jazz' || discipline === 'rock_pop' || discipline === 'electronic') {
    return 'concert'
  }
  if (type === 'performance' && (discipline === 'opera' || discipline === 'dance')) return 'opera'
  if (type === 'performance') return 'theatre'
  if (type === 'exhibition' || type === 'opening'
      || discipline === 'visual_art' || discipline === 'photography'
      || discipline === 'design' || discipline === 'architecture') return 'exhibition'
  if (type === 'screening' || type === 'release' || discipline === 'film') return 'film'
  if (type === 'market' || discipline === 'food') return 'market'
  // talk, tour, workshop, literature, history — and anything this list has
  // not met yet, the way `REST` catches anything no lane claims.
  return 'talk'
}

/** The chip label, and (WP 5.0) a cluster's own heading.
 *
 *  This is the copy to read. `Browse.tsx` had a private one of its own for a
 *  while — 5.0's brief defined these here and in the same breath forbade
 *  touching `Browse.tsx`, so for one release there really were two and they
 *  really could have drifted. WP 5.4 owned that file and deleted its pair
 *  in favour of these; the two had not drifted, so nothing else changed. */
export const KIND_LABEL: Record<Exclude<Kind, 'all'>, string> = {
  concert: 'Concerts', opera: 'Opera and dance', theatre: 'Theatre', exhibition: 'Exhibitions',
  film: 'Film', club: 'Club', gig: 'Gigs', quiz: 'Quizzes', talk: 'Talks', market: 'Markets and fairs',
}

/**
 * The one row kind word (`rowMeta.ts`'s `kindWord`) each section heading
 * already says, or null where the heading covers several and the word on the
 * row is what tells them apart — "Opera and dance" holds Opera, Dance and
 * Theatre rows, Exhibitions holds openings, Markets and fairs holds
 * festivals.
 */
const HEADING_WORD: Record<Exclude<Kind, 'all'>, string | null> = {
  concert: 'Concert', opera: null, theatre: 'Theatre', exhibition: 'Exhibition', film: 'Film',
  club: 'Club', gig: 'Gig', quiz: 'Quiz', talk: 'Talk', market: 'Market',
}

/**
 * Does a row under this kind heading still need its kind word (a1-13)?
 *
 * "Concert" printed on every row under a "Concerts" heading said nothing the
 * heading had not, and cost the row's kind line the width its genre then
 * lost ("Concert · O… · Sat 24 Oct" at 390px). Only where the heading names
 * exactly that word: a row whose word differs from its heading's ("Opening"
 * under Exhibitions) keeps it. `heading` null — no heading above the row —
 * always keeps it.
 */
export function kindWordShown(word: string, heading: Exclude<Kind, 'all'> | null | undefined): boolean {
  if (!heading) return true
  return HEADING_WORD[heading] !== word
}

/** A discipline hue per chip — `rowMeta.ts`'s own token set, restated: that
 *  file may not import this one and this one has no reason to import a
 *  function that reads a whole row just to get a colour by kind. Lives beside
 *  `KIND_LABEL` above and carried the same warning until WP 5.4 deleted
 *  `Browse.tsx`'s own copy. */
export const KIND_DOT: Record<Exclude<Kind, 'all'>, string> = {
  concert: 'var(--d-concert)', opera: 'var(--d-opera)', theatre: 'var(--d-theatre)',
  exhibition: 'var(--d-exhib)', film: 'var(--d-film)', talk: 'var(--d-talk)', market: 'var(--d-market)',
  // A gig keeps the concert hue, as `hueOf` gives it; a club night has its own.
  club: 'var(--d-club)', gig: 'var(--d-concert)',
  // `rowMeta.ts`'s `hueOf` gives a quiz `--d-quiz` (tokens.css).
  quiz: 'var(--d-quiz)',
}

/** Has this film got a verdict worth leading with — any of the three scores
 *  is enough. `filmRank.ts` asks the identical question of a real row; this
 *  file may not import it, so the question is asked again here, on the one
 *  field (`film`) this file declares for exactly this reason. */
const hasFilmScore = (e: FeedRow): boolean =>
  !!(e.film?.rt_score || e.film?.metascore || e.film?.imdb_rating)

/**
 * Rows regrouped by chip, in `KINDS` order, keeping each group's incoming
 * order — except Film under a cap, which puts a scored film ahead of an
 * unscored one first (WP 6.3: a five-deep cap on a day with twelve films
 * should not spend its five slots on whichever five a recipe happened to
 * emit first). Empty groups are omitted — a Browse clustered by kind has no
 * reason to draw a heading over nothing, the same rule `groupByDay` follows
 * for a day with no rows in it.
 *
 * `cap` (0, the default) is no cap at all; where it trims a group, `hidden`
 * says by how much — the number `.morep`'s "N more" line reads, rather than
 * the rows just disappearing with no way to ask where they went.
 */
export function clusterByKind<T extends FeedRow>(
  rows: T[], opts: { cap?: number } = {},
): { kind: Exclude<Kind, 'all'>; rows: T[]; hidden: number }[] {
  const cap = opts.cap ?? 0
  const buckets = new Map<Exclude<Kind, 'all'>, T[]>()
  for (const e of rows) {
    const k = kindOf(e)
    const list = buckets.get(k)
    if (list) list.push(e); else buckets.set(k, [e])
  }
  return KINDS.filter(k => buckets.has(k)).map(k => {
    let all = buckets.get(k)!
    if (cap && k === 'film') {
      all = [...all].sort((a, b) => {
        const as = hasFilmScore(a), bs = hasFilmScore(b)
        if (as !== bs) return bs ? 1 : -1
        return (a.next_showing ?? '') < (b.next_showing ?? '') ? -1 : 1
      })
    }
    const shown = cap ? all.slice(0, cap) : all
    return { kind: k, rows: shown, hidden: all.length - shown.length }
  })
}

// ---------------------------------------------------------------------------
// Rank (WP 8.3: Browse ranks instead of grouping by day)
// ---------------------------------------------------------------------------

/** Whole days from one ISO date to another, pinned to noon UTC —
 *  `browseRank.ts`'s own `daysDiff` (WP 8.0), restated below for the same
 *  reason every other rule in this section is. */
const daysDiff = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000)

/** `browseRank.ts`'s own bar (WP 8.0), restated: a film at this score or
 *  above keeps its base rank even when its critic scores are lower — Doug's
 *  "something really worthwhile that maybe doesn't have a lot of reviews". */
const PICK_MIN = 0.7

/** `browseRank.ts`'s own `criticScore` (WP 8.0), restated: RT and Metacritic
 *  read out of 100, IMDb out of 10, averaged; null when the row carries
 *  none of the three. */
function criticScore(f: FeedRow['film']): number | null {
  if (!f) return null
  const scores: number[] = []
  if (f.rt_score != null) scores.push(f.rt_score / 100)
  if (f.metascore != null) scores.push(f.metascore / 100)
  if (f.imdb_rating != null) scores.push(f.imdb_rating / 10)
  if (scores.length === 0) return null
  return scores.reduce((a, b) => a + b, 0) / scores.length
}

/** `affinity.ts`'s own K5 constants (WP E4), restated: this file may not
 *  import that one, for the same reason it may not import `calendar.ts` —
 *  see the file header. `test/affinity.test.ts` cross-checks the values. */
const AFFINITY_PER_UNIT = 0.03
const AFFINITY_CAP = 0.10
const AFFINITY_DISMISS_PENALTY = 0.05

/** `affinity.ts`'s own shape, restated. */
export interface AffinityRow { positive: number; negative: number }
export type AffinityMap = Map<string, AffinityRow>

/** `affinity.ts`'s own `affinityLift`, restated (WP E4, K5): sum of positive
 *  weight over the row's distinct people and ensembles, capped, minus a flat
 *  penalty once if any of them was dismissed twice or more. See that file
 *  for the reasoning; `test/affinity.test.ts` asserts this restatement
 *  agrees with it number for number. */
export function affinityLift(e: FeedRow, affinity?: AffinityMap): number {
  if (!affinity) return 0
  const seen = new Set<string>()
  let positive = 0
  let dismissedTwice = false
  const entities: { key: string }[] = [
    ...(e.people ?? []).map(p => ({ key: `person:${p.id}` })),
    ...(e.ensembles ?? []).map(s => ({ key: `ensemble:${s.id}` })),
  ]
  for (const { key } of entities) {
    if (seen.has(key)) continue
    seen.add(key)
    const a = affinity.get(key)
    if (!a) continue
    positive += a.positive
    if (a.negative >= 2) dismissedTwice = true
  }
  let lift = Math.min(positive * AFFINITY_PER_UNIT, AFFINITY_CAP)
  if (dismissedTwice) lift -= AFFINITY_DISMISS_PENALTY
  return Math.max(-AFFINITY_DISMISS_PENALTY, Math.min(AFFINITY_CAP, lift))
}

/**
 * `browseRank.ts`'s own `browseRank` (WP 8.0), restated here rather than
 * imported — same reason every duplicated constant in this file gives:
 * both files are loaded directly by the Worker's test suite, whose ESM
 * resolver will not add an extension a bundler would, and `app/tsconfig.json`
 * does not set `allowImportingTsExtensions` to let a source file spell one
 * out itself (`away.ts`'s note on `reach.ts` is the identical trap).
 * `test/browse.test.ts` carries the same cross-check `LATE_HOUR` and
 * `STARTED_GRACE_MIN` already do between this file and `today.ts`: fixtures
 * run through both this function and the real one, asserting they agree —
 * strengthened in WP 8.3b to pass the identical fixture object to both
 * rather than a hand-mapped copy, so a divergence in shape or behaviour
 * cannot hide behind the mapping step.
 *
 * `RankRow` and this file's own `film` field now agree exactly: both read
 * a film's `de_release_on` off `e.film?.de_release_on`, because `feed_lite`
 * has no top-level column of that name — only the one `mergeFilmFacts`
 * writes inside `film` (see the note on `FeedRow` above). WP 8.3 caught this
 * here first; WP 8.3b fixed `browseRank.ts` itself, which until then read a
 * top-level `e.de_release_on` that was `undefined` on every real row and
 * never took the 14-day exception it exists for.
 *
 * Exported (WP 8.3b) so `test/browse.test.ts` can assert this restatement's
 * numbers against the real `browseRank`'s, row for row, rather than only
 * their sort order — every other caller stays inside this file; `Browse.tsx`
 * only ever reaches it through `rankedByKind`.
 */
export function browseRank(e: FeedRow, now: Date, affinity?: AffinityMap): number {
  const today = hereDay(now)
  let rank = scoreOf(e) + affinityLift(e, affinity)

  if (kindOf(e) === 'film') {
    const critic = criticScore(e.film)
    if (critic != null) {
      const blended = 0.55 * rank + 0.45 * critic
      const releaseDay = releaseOn(e.film) || e.published_on
      const daysToRelease = releaseDay ? daysDiff(today, releaseDay) : Infinity
      rank = Math.max(blended, (rank >= PICK_MIN || daysToRelease <= 14) ? rank : blended)
    }
  }

  // One urgency, from `rankBase.ts`. It used to be a `run_end` branch with
  // the near-showing lift as its `else`, which a row with a `run_end` — every
  // newsletter row — could never reach.
  rank += urgencyLift(e, today, zoneOfCity(e.city))

  if (e.run_start) {
    const daysToStart = daysDiff(today, e.run_start)
    if (daysToStart > 60) rank -= 0.08
  } else if (e.next_showing) {
    const daysToShowing = daysDiff(today, rowDay(e, e.next_showing))
    if (daysToShowing > 60) rank -= 0.08
  }

  return rank
}

/**
 * Rows regrouped by chip, in `KINDS` order, each sorted by `browseRank`
 * descending — the default events screen's own shape now that Browse stops
 * grouping by day (Doug: "what's the most interesting stuff upcoming in
 * general"). `cap` (0, the default) shows every row; where it trims a
 * group, `hidden` says by how much, the same number `.morep`'s "N more"
 * line reads from `clusterByKind`. Replacement is automatic: a saved,
 * dismissed or seen row is never in `rows` by the time this runs, so the
 * sixth becomes the fifth with nothing else for this function to do.
 */
/** The Berlin calendar day a club night falls on, for the Club sort: its
 *  next showing, read as a Berlin day — never `slice(0, 10)` on the
 *  timestamp, because a night with no printed time is stored as midnight
 *  Berlin, which is 22:00 UTC the evening before (SO36's whole programme,
 *  30 Sept). A recurring night (Cassiopeia's Super Tuesday, four dates)
 *  is its next date, not the run's first; a row with no showing falls back
 *  to the day the source printed, and one with neither sorts last. */
const nightOf = (e: FeedRow): string =>
  e.next_showing ? rowDay(e, e.next_showing) : (e.run_start ?? '9999-12-31')

/** The chips that sort by the evening, soonest first, rather than by rank:
 *  a club night, and a quiz (nightlife N6) — which has no score at all
 *  (`notRanked`), and whose question is only "which one is next". A
 *  scheduled quiz's `next_showing` is its next occurrence; a recipe's dated
 *  one is its own night. */
const BY_NIGHT: ReadonlySet<Exclude<Kind, 'all'>> = new Set(['club', 'quiz'])

export function rankedByKind<T extends FeedRow>(
  rows: T[], now: Date, opts: { cap?: number; affinity?: AffinityMap } = {},
): { kind: Exclude<Kind, 'all'>; rows: T[]; hidden: number }[] {
  const { cap = 0, affinity } = opts
  const buckets = new Map<Exclude<Kind, 'all'>, T[]>()
  for (const e of rows) {
    const k = kindOf(e)
    const list = buckets.get(k)
    if (list) list.push(e); else buckets.set(k, [e])
  }
  // Each row's rank once, not once per comparison: a sort calls its
  // comparator ~n log n times, and `browseRank` allocates (`affinityLift`'s
  // entity list, `toISOString`) — 1.4 s of a Save at CPU x4 (a4-13).
  const ranks = new Map<T, number>()
  const rankOf = (e: T): number => {
    let r = ranks.get(e)
    if (r === undefined) { r = browseRank(e, now, affinity); ranks.set(e, r) }
    return r
  }
  const nights = new Map<T, string>()
  const nightOfRow = (e: T): string => {
    let n = nights.get(e)
    if (n === undefined) { n = nightOf(e); nights.set(e, n) }
    return n
  }
  return KINDS.filter(k => buckets.has(k)).map(k => {
    const byRank = (a: T, b: T) => rankOf(b) - rankOf(a)
    // A club night is a night, not a run: the question is "what is on this
    // weekend", and a rank that put 28 Nov above 3 Oct because its bookings
    // scored higher (verify-6, 30 Sept) was answering a different one. So
    // Club sorts by the night, soonest first, and the rank only breaks ties
    // between the same night. Every other kind keeps the rank.
    const cmp = BY_NIGHT.has(k)
      ? (a: T, b: T) => nightOfRow(a).localeCompare(nightOfRow(b)) || byRank(a, b)
      : byRank
    const all = [...buckets.get(k)!].sort(cmp)
    const shown = cap ? all.slice(0, cap) : all
    return { kind: k, rows: shown, hidden: all.length - shown.length }
  })
}

// ---------------------------------------------------------------------------
// When (F2's first control)
// ---------------------------------------------------------------------------

/**
 * The five windows, in the order both shells draw them, and their words
 * (a4-14). The phone used to say Tonight · This week · Weekend · Any time and
 * the desk Tonight · This weekend · Next 7 days · Next 30 days · Any time, for
 * the same question; Doug chose the desk's five, worded the desk's way, on
 * both. The keys are the `?when=` values every link already writes — `week`
 * has always meant the next seven days, today included — so no link changed
 * meaning; `whenOf` below keeps the one retired spelling (`all`) working.
 */
export type When = 'tonight' | 'weekend' | 'week' | 'month' | 'any'

export const WINDOWS: readonly { when: When; label: string }[] = [
  { when: 'tonight', label: 'Tonight' },
  { when: 'weekend', label: 'This weekend' },
  { when: 'week', label: 'Next 7 days' },
  { when: 'month', label: 'Next 30 days' },
  { when: 'any', label: 'Any time' },
]

export const WHEN_LABEL: Record<When, string> =
  Object.fromEntries(WINDOWS.map(w => [w.when, w.label])) as Record<When, string>

/** Where each shell opens with no `?when=` (a4-14, Doug's choice): the phone
 *  on Any time — WP 8.3's "I care less about when in the week" — and the
 *  desk, which has a date column to read down, on Next 30 days. Two
 *  defaults on purpose, written down here beside the one list. */
export const PHONE_WHEN: When = 'any'
export const DESK_WHEN: When = 'month'

/** `?when=` read back, or null for no window named. `all` is the desk's old
 *  word for Any time (a4-09 wrote it on no link, but a hand-typed one keeps
 *  working). Null-prototype, so `constructor` is not a window (CLAUDE.md's
 *  ALIASES bug). */
const WHEN_IN: Record<string, When> = Object.assign(Object.create(null), {
  tonight: 'tonight', weekend: 'weekend', week: 'week', month: 'month', any: 'any', all: 'any',
})
export function whenOf(value: string | null | undefined): When | null {
  return (value && WHEN_IN[value]) || null
}

/** After this Berlin hour, Tonight is over — both the `tonight` window
 *  `inWindow` gates below and the "Tonight" suffix `DayHeading` draws on
 *  today's own heading. One constant, because the day turning over at 22:00
 *  is a single fact about the evening, not two: a plan that says "the old
 *  `TONIGHT_ENDS_HOUR = 23` cutoff goes" and a screen that still gates the
 *  Tonight chip on 23 while only the heading's word moved to 22 is exactly
 *  the bug CLAUDE.md catalogues at the top of its own list — one value (when
 *  tonight ends) that two call sites read from two different constants. WP
 *  5.2 is adding the same hour to `today.ts` as its own `LATE_HOUR`, in
 *  parallel on its own branch; the two will read the same literal until the
 *  manager's merge lets one import the other. */
export const LATE_HOUR = 22

/** A single-occurrence row already started more than this many minutes ago,
 *  on today, is out of every window but Any time — WP 5.2 is adding the same
 *  constant to `today.ts` (which cannot import this file yet: its branch
 *  does not exist), asserting the literal `30` with its own comment; the
 *  manager's merge makes the two sides check each other. A run is unaffected
 *  — it is still going, whatever its start said. */
export const STARTED_GRACE_MIN = 30

/** How many days "Next 7 days" looks ahead, today included. */
export const WEEK_DAYS = 7

/** How many days "Next 30 days" looks ahead, today included. */
export const MONTH_DAYS = 30

/** The Saturday and Sunday the week `now` sits in — `entries.ts`'s
 *  `weekendRange`, restated for the no-imports rule. Monday through Friday
 *  gets the coming weekend; Saturday gets itself plus tomorrow; Sunday gets
 *  just itself, because Saturday has already gone. */
function weekendDays(today: string): { from: string; to: string } {
  const dow = dayOfWeek(today)
  if (dow === 0) return { from: today, to: today }
  const sat = addDaysIso(today, (6 - dow + 7) % 7)
  return { from: sat, to: addDaysIso(sat, 1) }
}

/** The closed day range a `when` covers. Any time runs from today to a
 *  far date — what bounds it is whatever the store holds, not a rule in
 *  here. */
function windowRange(when: When, now: Date): { from: string; to: string } {
  const today = hereDay(now)
  if (when === 'tonight') return { from: today, to: today }
  if (when === 'weekend') return weekendDays(today)
  if (when === 'month') return { from: today, to: addDaysIso(today, MONTH_DAYS - 1) }
  if (when === 'any') return { from: today, to: '9999-12-31' }
  return { from: today, to: addDaysIso(today, WEEK_DAYS - 1) }
}

/** A row with two different ends is a run; `run_end === run_start` is a
 *  one-day row written the long way, same rule `today.ts`'s `runOf` uses. */
function runSpan(e: FeedRow): { start: string; end: string } | null {
  if (!e.run_start || !e.run_end || e.run_end === e.run_start) return null
  return { start: e.run_start, end: e.run_end }
}

/** The next occurrence still ahead, ignoring run markers. */
function nextStart(showings: Showing[], now: Date): string | null {
  const from = now.toISOString()
  let best: string | null = null
  for (const s of showings) {
    if (s.kind === 'run') continue
    if (s.starts_at < from) continue
    if (best === null || s.starts_at < best) best = s.starts_at
  }
  return best
}

/** The instant a non-run row's clock names — `next_showing`/`next_opening`,
 *  computed server-side "against `now()`" (`entries.ts`'s `alreadyHappened`)
 *  at whatever moment the view last ran, or a live scan of this row's own
 *  showings when neither is set. The server-computed pair is why this can be
 *  a timestamp already in the past by the time a client reads it: the row
 *  the view answered "still ahead" hours ago has since started. */
function startAt(e: FeedRow, ownShowings: Showing[], now: Date): string | null {
  return e.next_showing ?? e.next_opening ?? nextStart(ownShowings, now)
}

/** The one day a non-run row occurs on, or null for a row stating none. */
function occurrenceDay(e: FeedRow, ownShowings: Showing[], now: Date): string | null {
  const at = startAt(e, ownShowings, now)
  if (at) return rowDay(e, at)
  return e.run_start ?? e.published_on ?? null
}

/** Showings by entry, built once per showings array rather than once per
 *  `inWindow` call: `inWindow` is asked of every row, and rebuilding the index
 *  of ~6,300 showings for each of ~2,000 rows was 6.4 s of a When change at
 *  CPU x4 (a4-13). Keyed on the array's identity — the query hands the same
 *  array back until the showings themselves change. */
const byEntryCache = new WeakMap<Showing[], Map<string, Showing[]>>()
const byEntry = (showings: Showing[]): Map<string, Showing[]> => {
  const hit = byEntryCache.get(showings)
  if (hit) return hit
  const out = new Map<string, Showing[]>()
  for (const s of showings) {
    const list = out.get(s.entry_id)
    if (list) list.push(s); else out.set(s.entry_id, [s])
  }
  byEntryCache.set(showings, out)
  return out
}

/** Every showing a card stands on — its own and, for a set, its members'. */
function cardShowings(e: FeedRow, showings: Showing[]): Showing[] {
  const index = byEntry(showings)
  const ids = e.group_ids?.length ? e.group_ids : [e.id]
  if (ids.length === 1) return index.get(ids[0]) ?? []
  return ids.flatMap(id => index.get(id) ?? [])
}

/** Where a card sits inside a window: the first Berlin day of it, inside the
 *  window, that the card is on, and the instant it starts that day (null for
 *  a run or a row dated by day only). `day` null is Any time's answer for a
 *  row that states no date at all. */
export interface WindowHit { day: string | null; at: string | null }

/**
 * Is a card in the window, and under which day (a3-18, Doug's choice).
 *
 * A card is in every window where any of its showings fall, and listed under
 * its first day inside that window. It used to be in a window only by its
 * one next occurrence (`occurrenceDay`), so a film playing tonight and on
 * Saturday was in Tonight and missing from This weekend — VATERLAND on a
 * Friday: Tonight 2, This weekend 0, though its own pane listed three
 * Saturday showings. Counts follow from this, since both shells count the
 * cards it lets in.
 *
 * The instants considered are the card's own showings (not a run marker or
 * an annotation) and the clock the view computed (`next_showing`,
 * `next_opening`), which may be the only one when the showings have not
 * arrived. One already started on today by more than `STARTED_GRACE_MIN` is
 * over (Doug, 26 Sept), and so is one on a day already gone; a card whose
 * every instant is over is in no window. A card with no instant at all falls
 * back on the day it was published or starts.
 *
 * A run counts by overlap, under the window's first day it is on — still
 * going is still going, whatever its start said.
 */
export function windowHit(e: FeedRow, when: When, now: Date, showings: Showing[]): WindowHit | null {
  if (when === 'tonight' && hereHour(now) >= LATE_HOUR) return null
  const { from, to } = windowRange(when, now)
  const run = runSpan(e)
  if (run) {
    if (run.start > to || run.end < from) return null
    return { day: run.start > from ? run.start : from, at: null }
  }

  const today = hereDay(now)
  const instants: string[] = []
  for (const s of cardShowings(e, showings)) {
    if (s.kind !== 'run' && s.kind !== 'annotation') instants.push(s.starts_at)
  }
  if (e.next_showing) instants.push(e.next_showing)
  if (e.next_opening) instants.push(e.next_opening)

  if (instants.length === 0) {
    const day = e.run_start ?? e.published_on ?? null
    if (!day) return when === 'any' ? { day: null, at: null } : null
    return day >= from && day <= to ? { day, at: null } : null
  }

  let best: { day: string; at: string; t: number } | null = null
  for (const at of instants) {
    const t = new Date(at).getTime()
    if (Number.isNaN(t)) continue
    const day = rowDay(e, at)
    if (day < today || day < from || day > to) continue
    if (day === today && now.getTime() - t > STARTED_GRACE_MIN * 60_000) continue
    if (!best || t < best.t) best = { day, at, t }
  }
  return best ? { day: best.day, at: best.at } : null
}

/** Does this row belong in the given window — `windowHit`, answered yes or
 *  no. Any time never narrows: a row that has been let into the feed is in
 *  it, whatever its own clock says (the feed's `alreadyHappened` is what
 *  takes a finished row out). */
export function inWindow(e: FeedRow, when: When, now: Date, showings: Showing[]): boolean {
  if (when === 'any') return true
  return windowHit(e, when, now, showings) !== null
}

// ---------------------------------------------------------------------------
// Grouping (F3, F6)
// ---------------------------------------------------------------------------

/** How long a run has to be before it stops being a date and becomes a fact
 *  about the building — `rowMeta.ts`'s own `PERMANENT_DAYS` and `isPermanent`,
 *  restated for the no-imports rule. */
const PERMANENT_DAYS = 730

function isPermanent(e: FeedRow, now: Date): boolean {
  if (!e.run_end) return false
  const end = D(e.run_end).getTime()
  const start = e.run_start ? D(e.run_start).getTime() : now.getTime()
  if (end - start > PERMANENT_DAYS * 86400000) return true
  return Number(e.run_end.slice(0, 4)) > now.getUTCFullYear() + 1
}

/**
 * The one calendar day a row's heading sits under, for a row `groupByDay`
 * below has *not* already pulled into the runs group — so every run
 * reaching here started inside the window and anchors on its own start,
 * the day it is actually news on. A point-in-time row with no date at all
 * (rare; `occurrenceDay` returns null) anchors on `floor`, the window's own
 * first day, rather than being dropped.
 */
function anchorDay(e: FeedRow, now: Date, ownShowings: Showing[], floor: string): string {
  const run = runSpan(e)
  if (run) return run.start
  return occurrenceDay(e, ownShowings, now) ?? floor
}

/**
 * Rows, windowed and bucketed by day — F3's sticky headings, F6's permanent
 * exhibitions sorted to the end, and (WP 3.2c) a run already in progress
 * pulled into its own heading above the days.
 *
 * A run that started before the window opened and has not ended is not news
 * on the window's first day — it just happens to still be running — so it
 * gets no day heading at all; it lands in the `'runs'` group, the same kind
 * of thing F6's `'permanent'` group already is: real, worth showing, and
 * not a thing that happens on a particular day. This is what WP 3.2's own
 * `anchorDay` used to do by clamping a run's start to `floor` — which is
 * the bug this package exists to fix: clamping to the window's first day
 * makes every run in progress look like Friday's news, and a week that is
 * 83% exhibitions and films turns into one heading with 271 rows under it.
 * A run whose *start* falls inside the window is still real news on that
 * day, so it keeps its own day heading exactly as before.
 *
 * Order within a day is whatever order `rows` arrived in: the caller sorts
 * first (soonest, best, A–Z) and this only ever groups, so a stable bucket
 * keeps that order rather than deciding a second one.
 *
 * Generic in the row, the same reason `today.ts`'s `pickLeads` is: `Browse.tsx`
 * hands this real `FeedRow` objects, with every column `feed_lite` carries,
 * and a version of this that declared its return as the bare structural type
 * above would make every caller cast to get them back.
 */
export function groupByDay<T extends FeedRow>(
  rows: T[], when: When, now: Date, showings: Showing[],
): { day: string | 'permanent' | 'runs'; rows: T[] }[] {
  const showingsByEntry = byEntry(showings)
  const floor = when === 'any' ? hereDay(now) : windowRange(when, now).from

  const buckets = new Map<string, T[]>()
  const permanent: T[] = []
  const inProgress: T[] = []

  for (const e of rows) {
    if (!inWindow(e, when, now, showings)) continue
    if (isPermanent(e, now)) { permanent.push(e); continue }
    const run = runSpan(e)
    if (run && run.start < floor) { inProgress.push(e); continue }
    // a3-18: under the first day inside the window the card is on, which
    // for a film playing tonight and Saturday is Saturday in This weekend.
    const day = windowHit(e, when, now, showings)?.day
      ?? anchorDay(e, now, showingsByEntry.get(e.id) ?? [], floor)
    const list = buckets.get(day)
    if (list) list.push(e); else buckets.set(day, [e])
  }

  const out: { day: string | 'permanent' | 'runs'; rows: T[] }[] = []
  if (inProgress.length) out.push({ day: 'runs' as const, rows: inProgress })
  for (const day of [...buckets.keys()].sort()) out.push({ day, rows: buckets.get(day)! })
  if (permanent.length) out.push({ day: 'permanent' as const, rows: permanent })
  return out
}

/** The word after "N more <kind>" on a capped cluster's own line — its own,
 *  shorter vocabulary: `DayHeading`'s "Tonight"/"Tomorrow" suffix has no room
 *  on a one-line link, and a run already going reads "on now" to match the
 *  heading above it rather than repeating the phrase. `'permanent'` never
 *  reaches this — F6's own rows are never many enough to cap — so it answers
 *  with nothing rather than guessing at a case that cannot happen. */
export function morepDayLabel(day: string | 'permanent' | 'runs', today: string): string {
  if (day === 'runs') return 'on now'
  if (day === 'permanent') return ''
  if (day === today) return 'today'
  return `on ${D(day).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'long' })}`
}

// ---------------------------------------------------------------------------
// Mode (WP 6.3: Browse absorbs Places)
// ---------------------------------------------------------------------------

/** Browse's other half. `?mode=places` groups the same rows by building
 *  instead of by day — `router.ts`'s `#/places…` and `#/venues…` aliases
 *  write this key themselves now that Places is not its own tab. Anything
 *  else, including nothing at all, is Events: a stray or hand-edited
 *  `?mode=` should land on the mode Doug actually opens daily, not on a
 *  blank one. */
export type BrowseMode = 'events' | 'places'

export function modeOf(query: Record<string, string>): BrowseMode {
  return query.mode === 'places' ? 'places' : 'events'
}

/**
 * The dismissed rows the Hidden page can bring back: every `dismissed`
 * `entry_state` row whose entry is still in the feed, in state order.
 *
 * Browse's "Hidden · N" and the Hidden page both read this (a1-06). The row
 * used to count `dismissed.size` — every dismissal ever made — while the page
 * listed only the ones still in the feed, so "Hidden · 7" opened a page of
 * one: the other six had ended or been cancelled and had nothing left to
 * restore. The page's own reason for dropping them is the right one, so the
 * count follows it rather than the other way round.
 */
export function hiddenRows<E extends { id: string }>(
  stateRows: readonly { state: string; entry_id: string }[], feed: readonly E[],
): E[] {
  const byId = new Map(feed.map(e => [e.id, e]))
  const out: E[] = []
  for (const r of stateRows) {
    if (r.state !== 'dismissed') continue
    const e = byId.get(r.entry_id)
    if (e) out.push(e)
  }
  return out
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

/** Does this row answer the query — `search.ts`'s `matchesSearch`, the one
 *  haystack Today's box reads too (a1-05: both used to restate it, and both
 *  missed a film's directors). A blank query matches everything. */
export function matches(e: FeedRow & Searchable, q: string): boolean {
  return matchesSearch(e, q)
}
